import { Type } from '@angular/core';

export interface EntityEntry {
  label: string;
  icon: string;
  sourceModule: string;
  loadComponent: () => Promise<Type<any>>;
  loadModule: () => Promise<Type<any>>;
}

export const ENTITY_REGISTRY: EntityEntry[] = [
  // ── Shared entities (used across multiple modules) ───────────────────────
  {
    label: 'Calls',
    icon: 'pi pi-globe',
    sourceModule: 'shared',
    loadComponent: () =>
      import('../../modules/entities/call/call.component').then((m) => m.CallComponent),
    loadModule: () =>
      import('../../modules/entities/call/shared-calls.module').then((m) => m.SharedCallsModule),
  },
  {
    label: 'Ports',
    icon: 'pi pi-map-marker',
    sourceModule: 'shared',
    loadComponent: () =>
      import('../../modules/entities/port/port.component').then((m) => m.PortComponent),
    loadModule: () =>
      import('../../../modules/pda/pda.module').then((m) => m.PdaModule),
  },
  {
    label: 'Vessels',
    icon: 'pi pi-compass',
    sourceModule: 'shared',
    loadComponent: () =>
      import('../../modules/entities/vessel/vessel.component').then((m) => m.VesselComponent),
    loadModule: () =>
      import('../../../modules/pda/pda.module').then((m) => m.PdaModule),
  },
  {
    label: 'Vessel Types',
    icon: 'pi pi-info-circle',
    sourceModule: 'shared',
    loadComponent: () =>
      import('../../modules/entities/vessel-type/vessel-type.component').then((m) => m.VesselTypeComponent),
    loadModule: () =>
      import('../../../modules/pda/pda.module').then((m) => m.PdaModule),
  },
  {
    label: 'Cargo',
    icon: 'pi pi-box',
    sourceModule: 'shared',
    loadComponent: () =>
      import('../../modules/entities/grade/grade.component').then((m) => m.GradeComponent),
    loadModule: () =>
      import('../../../modules/pda/pda.module').then((m) => m.PdaModule),
  },
  {
    label: 'Clients',
    icon: 'pi pi-users',
    sourceModule: 'shared',
    loadComponent: () =>
      import('../../modules/entities/client/client.component').then((m) => m.ClientComponent),
    loadModule: () =>
      import('../../../modules/finance/finance.module').then((m) => m.FinanceModule),
  },
  {
    label: 'Vouchers',
    icon: 'pi pi-ticket',
    sourceModule: 'shared',
    loadComponent: () =>
      import('../../modules/entities/voucher/voucher.component').then((m) => m.VoucherComponent),
    loadModule: () =>
      import('../../../modules/da/da.module').then((m) => m.DaModule),
  },
  {
    label: 'Voucher Types',
    icon: 'pi pi-tag',
    sourceModule: 'shared',
    loadComponent: () =>
      import('../../modules/entities/voucher-type/voucher-type.component').then((m) => m.VoucherTypeComponent),
    loadModule: () =>
      import('../../../modules/pda/pda.module').then((m) => m.PdaModule),
  },
  {
    label: 'Countries',
    icon: 'pi pi-flag',
    sourceModule: 'shared',
    loadComponent: () =>
      import('../../modules/entities/country/country.component').then((m) => m.CountryComponent),
    loadModule: () =>
      import('../../../modules/da/da.module').then((m) => m.DaModule),
  },
  // ── PDA entities ─────────────────────────────────────────────────────────
  {
    label: 'PDAs',
    icon: 'pi pi-file',
    sourceModule: 'pda',
    loadComponent: () =>
      import('../../../modules/pda/components/quote/quote.component').then((m) => m.QuoteComponent),
    loadModule: () =>
      import('../../../modules/pda/pda.module').then((m) => m.PdaModule),
  },
  {
    label: 'Templates',
    icon: 'pi pi-clone',
    sourceModule: 'pda',
    loadComponent: () =>
      import('../../../modules/pda/components/templates/templates.component').then((m) => m.TemplatesComponent),
    loadModule: () =>
      import('../../../modules/pda/pda.module').then((m) => m.PdaModule),
  },
  {
    label: 'Instructions',
    icon: 'pi pi-list',
    sourceModule: 'pda',
    loadComponent: () =>
      import('../../../modules/pda/components/instruction/instruction.component').then((m) => m.InstructionComponent),
    loadModule: () =>
      import('../../../modules/pda/pda.module').then((m) => m.PdaModule),
  },
  {
    label: 'Instruction Groups',
    icon: 'pi pi-folder',
    sourceModule: 'pda',
    loadComponent: () =>
      import('../../../modules/pda/components/instruction-group/instruction-group.component').then((m) => m.InstructionGroupComponent),
    loadModule: () =>
      import('../../../modules/pda/pda.module').then((m) => m.PdaModule),
  },
  {
    label: 'Document Templates',
    icon: 'pi pi-folder',
    sourceModule: 'pda',
    loadComponent: () =>
      import('../../../modules/pda/components/document-templates/document-templates.component').then((m) => m.DocumentTemplatesComponent),
    loadModule: () =>
      import('../../../modules/pda/pda.module').then((m) => m.PdaModule),
  },
  {
    label: 'Commodities',
    icon: 'pi pi-tags',
    sourceModule: 'pda',
    loadComponent: () =>
      import('../../modules/entities/commodity/commodity.component').then((m) => m.CommodityComponent),
    loadModule: () =>
      import('../../../modules/pda/pda.module').then((m) => m.PdaModule),
  },

  // ── DA entities ──────────────────────────────────────────────────────────
  {
    label: 'DAs',
    icon: 'pi pi-receipt',
    sourceModule: 'da',
    loadComponent: () =>
      import('../../../modules/da/components/da/da.component').then((m) => m.DaComponent),
    loadModule: () =>
      import('../../../modules/da/da.module').then((m) => m.DaModule),
  },
  {
    label: 'DA Funds',
    icon: 'pi pi-send',
    sourceModule: 'da',
    loadComponent: () =>
      import('../../../modules/da/components/da-remittance/da-remittance.component').then((m) => m.DARemittanceComponent),
    loadModule: () =>
      import('../../../modules/da/da.module').then((m) => m.DaModule),
  },
  {
    label: 'Expenses',
    icon: 'pi pi-file',
    sourceModule: 'da',
    loadComponent: () =>
      import('../../../modules/da/components/da-voucher/da-voucher.component').then((m) => m.DAVoucherComponent),
    loadModule: () =>
      import('../../../modules/da/da.module').then((m) => m.DaModule),
  },
  {
    label: 'Invoices',
    icon: 'pi pi-file-edit',
    sourceModule: 'da',
    loadComponent: () =>
      import('../../../modules/da/components/invoice/invoice.component').then((m) => m.InvoiceComponent),
    loadModule: () =>
      import('../../../modules/da/da.module').then((m) => m.DaModule),
  },

  // ── Finance entities ─────────────────────────────────────────────────────
  {
    label: 'Receipts',
    icon: 'pi pi-money-bill',
    sourceModule: 'finance',
    loadComponent: () =>
      import('../../../modules/finance/components/receipt/receipt.component').then((m) => m.ReceiptComponent),
    loadModule: () =>
      import('../../../modules/finance/finance.module').then((m) => m.FinanceModule),
  },
  {
    label: 'FX Conversions',
    icon: 'pi pi-sync',
    sourceModule: 'finance',
    loadComponent: () =>
      import('../../../modules/finance/components/fx-conversion/fx-conversion.component').then((m) => m.FxConversionComponent),
    loadModule: () =>
      import('../../../modules/finance/finance.module').then((m) => m.FinanceModule),
  },
  {
    label: 'Exchange Variations',
    icon: 'pi pi-percentage',
    sourceModule: 'finance',
    loadComponent: () =>
      import('../../../modules/finance/components/da-exchange-variation/da-exchange-variation.component').then((m) => m.DAExchangeVariationComponent),
    loadModule: () =>
      import('../../../modules/finance/finance.module').then((m) => m.FinanceModule),
  },
  {
    label: 'Allocations',
    icon: 'pi pi-share-alt',
    sourceModule: 'finance',
    loadComponent: () =>
      import('../../../modules/finance/components/receipt-allocation/receipt-allocation.component').then((m) => m.ReceiptAllocationComponent),
    loadModule: () =>
      import('../../../modules/finance/finance.module').then((m) => m.FinanceModule),
  },
  {
    label: 'Companies',
    icon: 'pi pi-building',
    sourceModule: 'finance',
    loadComponent: () =>
      import('../../../modules/finance/components/company/company.component').then((m) => m.CompanyComponent),
    loadModule: () =>
      import('../../../modules/finance/finance.module').then((m) => m.FinanceModule),
  },
  {
    label: 'Suppliers',
    icon: 'pi pi-box',
    sourceModule: 'finance',
    loadComponent: () =>
      import('../../../modules/finance/components/supplier/supplier.component').then((m) => m.SupplierComponent),
    loadModule: () =>
      import('../../../modules/finance/finance.module').then((m) => m.FinanceModule),
  },
  {
    label: 'Bank Accounts',
    icon: 'pi pi-wallet',
    sourceModule: 'finance',
    loadComponent: () =>
      import('../../../modules/finance/components/bank-account/bank-account.component').then((m) => m.BankAccountComponent),
    loadModule: () =>
      import('../../../modules/finance/finance.module').then((m) => m.FinanceModule),
  },

  // ── Bill of Lading entities ──────────────────────────────────────────────
  {
    label: 'Shipowners',
    icon: 'pi pi-building',
    sourceModule: 'bill-of-lading',
    loadComponent: () =>
      import('../../../modules/bill-of-lading/components/shipowners/shipowners.component').then((m) => m.ShipownersComponent),
    loadModule: () =>
      import('../../../modules/bill-of-lading/bill-of-lading.module').then((m) => m.BillOfLadingModule),
  },
  {
    label: 'Drafts',
    icon: 'pi pi-file',
    sourceModule: 'bill-of-lading',
    loadComponent: () =>
      import('../../../modules/bill-of-lading/components/drafts/drafts.component').then((m) => m.DraftsComponent),
    loadModule: () =>
      import('../../../modules/bill-of-lading/bill-of-lading.module').then((m) => m.BillOfLadingModule),
  },

  // ── Legal entities ───────────────────────────────────────────────────────
  {
    label: 'Infractions',
    icon: 'pi pi-list',
    sourceModule: 'legal',
    loadComponent: () =>
      import('../../../modules/legal/components/legal-user-view/legal-user-view.component').then((m) => m.LegalUserViewComponent),
    loadModule: () =>
      import('../../../modules/legal/legal.module').then((m) => m.LegalModule),
  },
  {
    label: 'Notice Infractions',
    icon: 'pi pi-exclamation-circle',
    sourceModule: 'legal',
    loadComponent: () =>
      import('../../../modules/legal/components/noticeInfraction/notice-infraction.component').then((m) => m.NoticeInfractionComponent),
    loadModule: () =>
      import('../../../modules/legal/legal.module').then((m) => m.LegalModule),
  },
  {
    label: 'Offenders',
    icon: 'pi pi-users',
    sourceModule: 'legal',
    loadComponent: () =>
      import('../../../modules/legal/components/offender/offender.component').then((m) => m.OffenderComponent),
    loadModule: () =>
      import('../../../modules/legal/legal.module').then((m) => m.LegalModule),
  },
  {
    label: 'Infraction Types',
    icon: 'pi pi-tag',
    sourceModule: 'legal',
    loadComponent: () =>
      import('../../../modules/legal/components/infraction-type/infraction-type.component').then((m) => m.InfractionTypeComponent),
    loadModule: () =>
      import('../../../modules/legal/legal.module').then((m) => m.LegalModule),
  },
  {
    label: 'Infraction Motives',
    icon: 'pi pi-book',
    sourceModule: 'legal',
    loadComponent: () =>
      import('../../../modules/legal/components/infraction-motive/infraction-motive.component').then((m) => m.InfractionMotiveComponent),
    loadModule: () =>
      import('../../../modules/legal/legal.module').then((m) => m.LegalModule),
  },
  {
    label: 'Branches',
    icon: 'pi pi-building',
    sourceModule: 'legal',
    loadComponent: () =>
      import('../../../modules/legal/components/branch/branch.component').then((m) => m.BranchComponent),
    loadModule: () =>
      import('../../../modules/legal/legal.module').then((m) => m.LegalModule),
  },
  {
    label: 'Approval Workflow',
    icon: 'pi pi-sitemap',
    sourceModule: 'legal',
    loadComponent: () =>
      import('../../../modules/legal/components/approval-workflow-config/approval-workflow-config.component').then((m) => m.ApprovalWorkflowConfigComponent),
    loadModule: () =>
      import('../../../modules/legal/legal.module').then((m) => m.LegalModule),
  },
];
